import { GetServerSideProps, GetServerSidePropsResult } from 'next';
import dynamic from 'next/dynamic';
import { HesselSiteProps } from '../../../../../components/site/hessel-site.component';
import { getPage, getSiteSettings, umbraco } from '../../../../../lib/api';
import { ShopProductDetails } from '../../../../../lib/api/models/shop';
import { getShopProduct } from '../../../../../lib/api/shop/shop-product-api';
import { getProtocol, getShopPdpMetaDescription, getShopPdpMetaTitle } from '../../../../../utils/helpers';

type Props = {
    page: umbraco.Page;
    siteSettings: umbraco.SiteSettings;
    product: ShopProductDetails;
    productFamily: Array<ShopProductDetails>;
};

const DynamicHesselSite = dynamic<HesselSiteProps>(() =>
    import('../../../../../components/site/hessel-site.component').then((module) => module.HesselSite)
);

const page = (props: Props): JSX.Element => {
    return (
        <DynamicHesselSite
            page={{
                ...props.page,
                metaTitle: getShopPdpMetaTitle(props.product),
                metaDescription: getShopPdpMetaDescription(props.product),
            }}
            siteSettings={props.siteSettings}
            shopProduct={props.product}
            shopProductFamily={props.productFamily}
        />
    );
};

export const getServerSideProps: GetServerSideProps = async (context): Promise<GetServerSidePropsResult<Props>> => {
    const variantId = context.query.variantId;
    const parentId = context.query.parentId;

    const [[siteSettings, siteSettingsError], [page, pageError], [productFilterResponse, productError]] = await Promise.all([
        getSiteSettings(),
        getPage('shop/produkt'),
        getShopProduct(parentId.toString()),
    ]);

    if (!productFilterResponse || !Array.isArray(productFilterResponse.products)) {
        return {
            notFound: true,
        };
    }
    const variant = productFilterResponse.products.find((x) => x.productNumber === variantId);

    const protocol = getProtocol(context.req.headers.host?.toString() ?? '');
    const canonical = `${protocol}${context.req.headers.host}${variant?.canonicalUrl}`;

    if (page && siteSettings && productFilterResponse && variant && !siteSettingsError && !pageError && !productError) {
        const validUrl = context.resolvedUrl.split('?')[0] === `${variant.url}`;
        if (validUrl) {
            context.res.setHeader('Cache-Tag', [parentId as string, variantId as string]);
            return {
                props: {
                    page: variant.canonicalUrl ? { ...page, canonical } : { ...page },
                    siteSettings,
                    product: variant,
                    productFamily: productFilterResponse.products,
                },
            };
        }
    }
    return {
        notFound: true,
    };
};

export default page;
